import type { Json } from '~/types/database.types'
import type { TweetEntities, TweetUrl, TweetMention } from '~/types/tweet'

interface EntityWithIndices {
  start: number
  end: number
  type: 'url' | 'mention' | 'hashtag'
  data: TweetUrl | TweetMention | { tag: string }
}

/**
 * Processes tweet text and entities to create HTML with proper links
 * @param text The raw tweet text
 * @param entities The tweet entities containing urls and mentions
 * @returns HTML string with proper links
 */
export const processTweetText = (text: string, entities: Json): string => {
  // Type guard for entities
  if (typeof entities !== 'object' || !entities) {
    return text;
  }

  // Convert Json to TweetEntities type
  const tweetEntities = entities as TweetEntities;

  // Strip out t.co URL from the end if present
  let processedText = text;
  const tcoMatch = text.match(/https:\/\/t\.co\/\w+$/);
  if (tcoMatch) {
    processedText = text.substring(0, tcoMatch.index).trim();
  }

  // Collect all entities that need to be processed
  const allEntities: EntityWithIndices[] = [];

  // Add URLs (excluding media URLs)
  if (Array.isArray(tweetEntities.urls)) {
    tweetEntities.urls
      .filter(url => !url.display_url.includes('pic.twitter.com') && !url.display_url.includes('pic.x.com'))
      .forEach(url => {
        // Skip if this is the URL we stripped from the end
        if (tcoMatch && url.url === tcoMatch[0]) return;

        allEntities.push({
          start: url.start,
          end: url.end,
          type: 'url',
          data: url
        });
      });
  }

  // Add mentions
  if (Array.isArray(tweetEntities.mentions)) {
    tweetEntities.mentions.forEach(mention => {
      allEntities.push({
        start: mention.start,
        end: mention.end,
        type: 'mention',
        data: mention
      });
    });
  }

  // Add hashtags
  if (Array.isArray(tweetEntities.hashtags)) {
    tweetEntities.hashtags.forEach(hashtag => {
      allEntities.push({
        start: hashtag.start,
        end: hashtag.end,
        type: 'hashtag',
        data: hashtag
      });
    });
  }

  if (allEntities.length === 0) {
    return processedText;
  }

  // Convert the text to an array of characters to handle emoji and special characters correctly
  const chars = Array.from(processedText);

  // Sort all entities by start index in descending order to replace from end to start
  allEntities.sort((a, b) => b.start - a.start);

  // Replace each entity with its HTML link
  for (const entity of allEntities) {
    let htmlElement = '';

    switch (entity.type) {
      case 'url': {
        const url = entity.data as TweetUrl;
        const linkText = url.display_url;
        const href = url.unwound_url || url.expanded_url;
        htmlElement = `<a href="${href}" class="text-cardinal-400 underline hover:!text-cardinal" target="_blank">${linkText}</a>`;
        break;
      }
      case 'mention': {
        const mention = entity.data as TweetMention;
        htmlElement = `<a href="https://twitter.com/${mention.username}" class="text-cardinal-400 underline hover:!text-cardinal tweet-mention" target="_blank">@${mention.username}</a>`;
        break;
      }
      case 'hashtag': {
        const hashtag = entity.data as { tag: string };
        htmlElement = `<a href="https://twitter.com/hashtag/${hashtag.tag}" class="text-cardinal-400 underline hover:!text-cardinal tweet-hashtag" target="_blank">#${hashtag.tag}</a>`;
        break;
      }
    }

    // Replace the entity in the character array
    chars.splice(entity.start, entity.end - entity.start, ...Array.from(htmlElement));
  }

  return chars.join('');
}
